import { Link } from 'react-router-dom';
import CategoryBadge from '@/components/base/CategoryBadge';
import type { Topic } from '@/mocks/topics';

interface HeroProps {
  featured: Topic;
  secondary: Topic[];
}

export default function Hero({ featured, secondary }: HeroProps) {
  return (
    <section className="w-full border-b border-background-300 bg-background-100">
      <div className="mx-auto max-w-[1200px] px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <article className="animate-rise lg:col-span-7">
            <Link to={`/topics/${featured.slug}`} className="group block">
              <div className="relative h-[260px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[380px]">
                <img
                  src={featured.cover}
                  alt={`${featured.title} - 社会爆料焦点议题配图`}
                  title={`${featured.title} - 社会爆料`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-4 top-4 inline-flex items-center gap-1 rounded-md bg-primary-600 px-3 py-1.5 font-label text-xs text-background-50">
                  <i className="ri-focus-3-line" aria-hidden="true"></i>
                  本期焦点
                </span>
              </div>
            </Link>
            <div className="mt-5">
              <div className="flex flex-wrap items-center gap-3">
                <CategoryBadge category={featured.category} />
                <span className="font-label text-xs text-foreground-500">{featured.date}</span>
                <span className="font-label text-xs text-foreground-500">
                  {featured.readTime} 分钟阅读
                </span>
              </div>
              <h1 className="mt-3 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
                <Link to={`/topics/${featured.slug}`} className="transition-colors hover:text-primary-700">
                  {featured.title}
                </Link>
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                {featured.summary}
              </p>
              <Link
                to={`/topics/${featured.slug}`}
                className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-700"
              >
                阅读完整盘点
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </article>

          <aside className="animate-rise-delay-1 lg:col-span-5">
            <div className="flex items-center justify-between border-b border-background-300 pb-3">
              <h2 className="font-heading text-lg font-bold text-foreground-950">同期追踪</h2>
              <Link
                to="/topics"
                className="font-label text-sm text-primary-700 transition-colors hover:text-primary-800"
              >
                全部议题
              </Link>
            </div>
            <ul className="mt-1 divide-y divide-background-300">
              {secondary.map((topic, index) => (
                <li key={topic.slug}>
                  <Link
                    to={`/topics/${topic.slug}`}
                    className="group flex items-start gap-4 py-4"
                  >
                    <span className="font-heading text-2xl font-black text-background-400 transition-colors group-hover:text-primary-500">
                      {String(index + 2).padStart(2, '0')}
                    </span>
                    <span className="flex flex-col">
                      <span className="mb-1">
                        <CategoryBadge category={topic.category} size="sm" />
                      </span>
                      <span className="font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                        {topic.title}
                      </span>
                      <span className="mt-1 line-clamp-1 text-xs text-foreground-500">
                        {topic.summary}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </div>
    </section>
  );
}